import React from 'react';
import { AppOption, Platform, Format, Link, TrackEvent, LinkFields } from '../../types';
import { PLATFORM_OPTIONS, AD_ENVIRONMENT_OPTIONS } from '../../constants';
import { formatTrackerToken } from '../../helpers/formatTrackerToken';
import { formatLink } from '../../helpers/formatLink';
import { useForm } from '../../hooks/useForm';
import { Checkbox } from '../Common/Checkbox';
import { Field } from './Field';
import { FieldPath } from './FieldPath';
import { FieldAppTracker } from './FieldAppTracker';
import { Select } from './Select';
import { Input } from './Input';
import { Info } from './Info';

interface FormProps {
  editFields: LinkFields | undefined;
  appsOptions: AppOption[];
  onAppChange: (token: string) => void;
  onTrackEvent: TrackEvent;
  onCreate: (link: Omit<Link, 'id'>) => Promise<void>;
  onUpdate: (id: number, link: Omit<Link, 'id'>) => Promise<void>;
  onCancel: () => void;
}

export const Form: React.FC<FormProps> = ({
  editFields,
  appsOptions,
  onAppChange,
  onTrackEvent,
  onCreate,
  onUpdate,
  onCancel,
}) => {
  const {
    form,
    platform,
    adEnvironment,
    formats,
    format,
    targetAppsOptions,
    androidAppsOptions,
    targetAppOption,
    androidAppOption,
    targetTrackerOption,
    iosUniversalLinkPath,
    androidDeeplinkPathSameAsIos,
  } = useForm({ editFields, appsOptions });
  const androidAppSchemePrefilled = !!androidAppOption?.androidAppScheme;

  const handleSubmit = form.handleSubmit(async data => {
    onTrackEvent('generate_deeplink');

    const { title, trackerToken } = formatTrackerToken([
      [data.targetTracker, data.targetTrackerManual, targetAppOption],
      [data.androidTracker, data.androidTrackerManual, androidAppOption],
    ]);

    const link: Omit<Link, 'id'> = {
      title,
      ad_environment: adEnvironment,
      url: formatLink({
        ...data,
        additionalParameters: targetTrackerOption?.additionalParameters,
        trackerToken,
        androidAppSchemePrefilled,
        dataResidency: targetAppOption?.dataResidency,
      }),
    };

    if (editFields?.id !== undefined) {
      await onUpdate(editFields.id, link);
    } else {
      await onCreate(link);
    }
  });

  const handleTargetAppChange = (value: string) => {
    form.clearErrors(['targetApp', 'targetTracker']);
    onTrackEvent('platform_dropdown', { value });
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    // type="button" attribute should prevent form submission but there is an issue in react.
    // See https://github.com/facebook/react/issues/8554#issuecomment-473140672 for details.
    e.preventDefault();
    onCancel();
  };

  return (
    <form className="flex-auto flex-box-column" onSubmit={handleSubmit}>
      <div className="scrollable">
        <Field
          form={form}
          name="platform"
          label="Platform"
          component={<Select options={PLATFORM_OPTIONS} onChange={handleTargetAppChange} />}
        />

        {[Platform.ios, Platform.multi].includes(platform) ? (
          <div className="form-row form-row-group">
            <Field
              form={form}
              name="adEnvironment"
              label="Ad Environment"
              component={
                <Select
                  placeholder="--- Select an Ad Environment ---"
                  options={AD_ENVIRONMENT_OPTIONS}
                  onChange={value => onTrackEvent('adEnvironment_dropdown', { value })}
                />
              }
            />

            <Field
              form={form}
              name="format"
              label="Format"
              component={
                <Select
                  placeholder={
                    adEnvironment ? '--- Select a Format ---' : '--- You must select an Ad Environment first ---'
                  }
                  options={formats}
                  onChange={value => onTrackEvent('format_dropdown', { value })}
                />
              }
            />
          </div>
        ) : (
          <input ref={form.register} type="hidden" name="format" />
        )}

        <FieldAppTracker
          form={form}
          appName="targetApp"
          trackerName="targetTracker"
          manualTrackerName="targetTrackerManual"
          appsOptions={targetAppsOptions}
          appOption={targetAppOption}
          onAppChange={onAppChange}
          onTrackEvent={onTrackEvent}
        />

        {platform === Platform.multi && (!targetAppOption || targetAppOption.platform === Platform.ios) && (
          <FieldAppTracker
            form={form}
            appName="androidApp"
            trackerName="androidTracker"
            manualTrackerName="androidTrackerManual"
            labelPrefix="Android "
            appsOptions={androidAppsOptions}
            appOption={androidAppOption}
            onAppChange={onAppChange}
            onTrackEvent={onTrackEvent}
          />
        )}

        {[Format.ulink, Format.jsr].includes(format) && (
          <Field
            form={form}
            name="rawUniversalLink"
            label="Raw Universal Link"
            isReadOnly={!!targetAppOption?.rawUniversalLink}
            component={<Input defaultValue={targetAppOption?.rawUniversalLink} />}
          />
        )}

        {platform === Platform.ios && format === Format.tdl && (
          <Field
            form={form}
            name="iosAppScheme"
            label="iOS App Scheme"
            isReadOnly={!!targetAppOption?.iosAppScheme}
            component={<Input defaultValue={targetAppOption?.iosAppScheme} />}
            info="For example, my_app://"
          />
        )}

        {(platform === Platform.android ||
          (platform === Platform.multi && [Format.ulink, Format.jsr].includes(format))) && (
          <Field
            form={form}
            name="androidAppScheme"
            label="Android App Scheme"
            isReadOnly={androidAppSchemePrefilled}
            component={<Input defaultValue={androidAppOption?.androidAppScheme} />}
            info="For example, my_app://"
          />
        )}

        {platform === Platform.multi && format === Format.tdl && (
          <Field
            form={form}
            name="commonAppScheme"
            label="iOS/Android App Scheme"
            component={<Input />}
            info="For example, my_app://"
          />
        )}

        {((platform === Platform.ios && format === Format.tdl) || [Format.ulink, Format.jsr].includes(format)) && (
          <FieldPath
            form={form}
            name="iosUniversalLinkPath"
            enforceName="iosUniversalLinkPathEnforce"
            label={format === Format.tdl ? 'iOS Deeplink Path' : 'iOS Universal Link Path'}
            onTrackEvent={onTrackEvent}
          />
        )}

        {(platform === Platform.android ||
          (platform === Platform.multi && [Format.ulink, Format.jsr].includes(format))) && (
          <FieldPath
            form={form}
            name="androidDeeplinkPath"
            enforceName="androidDeeplinkPathEnforce"
            label="Android Deeplink Path"
            isReadOnly={androidDeeplinkPathSameAsIos}
            labelAside={
              platform === Platform.multi &&
              !!iosUniversalLinkPath && (
                <Checkbox ref={form.register} name="androidDeeplinkPathSameAsIos" label="Same as iOS Path" />
              )
            }
            onTrackEvent={onTrackEvent}
          />
        )}

        {platform === Platform.multi && format === Format.tdl && (
          <FieldPath
            form={form}
            name="commonDeeplinkPath"
            enforceName="commonDeeplinkPathEnforce"
            label="iOS/Android Path"
            onTrackEvent={onTrackEvent}
          />
        )}

        <div className="form-row form-row-group flex-box-row-wrap">
          <Field form={form} name="campaign" label="Campaign" isOptional component={<Input />} />

          <Field form={form} name="adgroup" label="Adgroup" isOptional component={<Input />} />

          <Field form={form} name="creative" label="Creative" isOptional component={<Input />} />

          <Info>If you don{"'"}t need any campaign information, leave empty.</Info>
        </div>

        <Field
          form={form}
          name="label"
          label="Label"
          isOptional
          info="The label parameter lets you send any custom data you can receive later via callbacks"
          component={<Input onFocus={() => onTrackEvent('input_label')} />}
        />

        <Field
          form={form}
          name="fallbackUrl"
          label="Fallback Url"
          info="This is not supported on macOS. To redirect Mac desktop users from your Adjust tracker, specify the REDIRECT MACOS URL."
          isOptional
          component={<Input onFocus={() => onTrackEvent('input_fallbackUrl')} />}
        />

        <Field
          form={form}
          name="redirectUrl"
          label="Redirect Url"
          isOptional
          component={<Input onFocus={() => onTrackEvent('input_redirectUrl')} />}
        />

        <Field
          form={form}
          name="redirectMacos"
          label="Redirect MacOS URL"
          info="Specify this value to redirect Mac desktop users from your Adjust tracker to a specific landing page. The FALLBACK URL is not supported on macOS."
          isOptional
          component={<Input onFocus={() => onTrackEvent('input_redirectMacos_url')} />}
        />

        <Field
          form={form}
          name="redirectIos"
          label="Redirect iOS Url"
          isOptional
          component={<Input onFocus={() => onTrackEvent('input_redirectIos_url')} />}
        />

        <Field
          form={form}
          name="redirectAndroid"
          label="Redirect Android Url"
          isOptional
          component={<Input onFocus={() => onTrackEvent('input_redirectAndroid_url')} />}
        />

        {[Platform.ios, Platform.multi].includes(platform) && (
          <Field
            form={form}
            name="idfa"
            label="Device ID MACROs (idfa)"
            isOptional
            component={<Input onFocus={() => onTrackEvent('input_device_id_idfa')} />}
          />
        )}

        {[Platform.android, Platform.multi].includes(platform) && (
          <Field
            form={form}
            name="gpsAdid"
            label="Device ID MACROs (gps_adid)"
            isOptional
            component={<Input onFocus={() => onTrackEvent('input_device_id_gpsadid')} />}
          />
        )}
      </div>

      <div className="form-row form-row-group form-action">
        {editFields ? (
          <>
            <button type="button" onClick={handleCancel} className="btn form-row">
              Cancel
            </button>
            <button type="submit" className="btn form-row">
              Save
            </button>
          </>
        ) : (
          <button type="submit" className="btn btn-block">
            Generate Deeplink
          </button>
        )}
      </div>
    </form>
  );
};
